import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type ArteChamada, type Chamada, type ConfigChamada } from '../api.js';

/**
 * Chamadas: o convite para o grupo que sai todo dia, com arte e texto em
 * rodízio — no WhatsApp (grupo e canal), nos Stories e no Feed.
 *
 * Fica na Agenda pelo mesmo motivo do piloto: o resultado é fila. Cada
 * ocorrência aparece abaixo como post normal, com a etiqueta 📣 chamada.
 */

const LINK_GRUPO = 'https://chat.whatsapp.com/CpUbHdKrhKb3n7BqQbd7A9';

/** Ponto de partida de uma chamada nova — tudo editável antes de ligar. */
const PADRAO: ConfigChamada = {
  name: 'Convite para o Grupo VIP',
  link: LINK_GRUPO,
  // Modelo pedido pelo dono (adaptado de um grupo de referência): missão, o
  // "envia pra amiga", o aviso dos contatos e o pedido de notificação. O link
  // do convite entra sozinho no fim, então o texto não repete a URL.
  whatsappTexts: [
    'Tem chuva de promoções aqui todos os dias e *muitas acabam em poucos minutos*! 👀⏰\n\nNossa missão é reunir os *melhores achadinhos de casa e beleza, de lojas confiáveis*, para você comprar com tranquilidade e pagar menos. 💸\n\n*💖 Conhece alguma amiga que ama economizar?* Envia o convite do Grupo VIP para ela também 👇\n\n📌 *Importante: adicione o número do administrador nos contatos para ver todos os links corretamente.*\n\n🛍️ Já deixa as notificações ligadas e aproveita as próximas ofertas! 📣',
    '⏰ Cupom bom e bug de preço *acabam em minutos* — e no Grupo VIP você é avisada primeiro!\n\nSó entra aqui oferta de *loja confiável*, garimpada a dedo pra você pagar menos. 💸\n\n*💖 Tem uma amiga que ama um achadinho?* Manda o convite pra ela entrar também 👇\n\n📌 *Adicione o número do administrador nos contatos para os links aparecerem direitinho.*\n\n🔔 Notificações ligadas = nenhuma promoção perdida! 📣',
    '💸 Garimpar promoção boa dá trabalho… *a gente faz isso por você!*\n\nTodo dia, os melhores achadinhos de casa e beleza da Shopee, de *lojas confiáveis*, no nosso Grupo VIP. 🛍️\n\n*💖 Conhece alguém que ama economizar?* Envia o grupo pra ela também 👇\n\n📌 *Importante: adicione o número do administrador nos contatos para visualizar todos os links.*\n\n🔔 Deixa as notificações ligadas e aproveita as próximas ofertas! 📣',
  ],
  // Sem prometer DM: a automação de respostas ainda não está no ar. O comentário
  // é pedido pelo alcance; o caminho de verdade é o link na bio.
  feedCaptions: [
    'Tem chuva de promoções no nosso Grupo VIP todos os dias — e muitas acabam em poucos minutos! 👀⏰\n\nNossa missão é reunir os melhores achadinhos de casa e beleza, de lojas confiáveis, para você comprar com tranquilidade e pagar menos 💸\n\n💖 Conhece uma amiga que ama economizar? Marca ela aqui!\n\n🔗 Link do grupo na bio 👆 e comenta GRUPO se você já entrou!',
    'Cupom bom e bug de preço acabam em minutos ⏰ No Grupo VIP você é avisada primeiro!\n\nSó oferta de loja confiável, garimpada a dedo pra você pagar menos 💸\n\n💖 Marca a amiga que ama um achadinho!\n\n🔗 Link na bio 👆 · comenta GRUPO se você já entrou',
  ],
  whatsappTimes: ['08:00', '12:30', '20:00'],
  // Stories fora: a API não põe o sticker de link, e Story não tem comentário.
  storyTimes: [],
  feedTimes: ['11:00'],
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function mensagemDe(cause: unknown): string {
  if (cause instanceof ApiError && cause.issues?.length) {
    return cause.issues.map((i) => i.message).join(' · ');
  }
  return cause instanceof Error ? cause.message : 'Algo deu errado';
}

/**
 * Formato de uma arte pelo nome do arquivo.
 *
 * Um arraste de vários arquivos precisa saber qual é qual sem perguntar: o
 * sufixo no nome (`-story`, `-feed`, `-quadrado`) diz o formato, e o resto do
 * nome agrupa os três arquivos da mesma arte.
 */
export function classificarArquivo(nome: string): { base: string; formato: 'square' | 'feed' | 'story' } {
  const semExt = nome.replace(/\.[a-z0-9]+$/i, '');
  const m = /^(.*?)[-_ ]?(story|stories|9x16|feed|4x5|quadrado|square|1x1)$/i.exec(semExt);
  if (!m) return { base: semExt, formato: 'square' };
  const tag = m[2]!.toLowerCase();
  const formato = /story|stories|9x16/.test(tag) ? 'story' : /feed|4x5/.test(tag) ? 'feed' : 'square';
  return { base: m[1] || semExt, formato };
}

export function CalloutPanel({ onMudou }: { onMudou: () => void }) {
  const [chamadas, setChamadas] = useState<Chamada[] | null>(null);
  const [form, setForm] = useState<ConfigChamada | null>(null);
  const [editando, setEditando] = useState<string | 'nova' | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);
  const arquivosRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    const lista = await api.chamadas();
    setChamadas(lista);
    return lista;
  }, []);

  useEffect(() => {
    void carregar().catch(() => setChamadas([]));
  }, [carregar]);

  if (!chamadas) return null;

  async function rodar(chave: string, fn: () => Promise<string | null>) {
    setOcupado(chave);
    setAviso(null);
    try {
      const texto = await fn();
      if (texto) setAviso({ ok: true, texto });
      await carregar();
      onMudou();
    } catch (cause) {
      setAviso({ ok: false, texto: mensagemDe(cause) });
    } finally {
      setOcupado(null);
    }
  }

  function abrirForm(c: Chamada | null) {
    setForm(
      c
        ? {
            name: c.name,
            link: c.link,
            whatsappTexts: c.whatsappTexts,
            feedCaptions: c.feedCaptions,
            whatsappTimes: c.whatsappTimes,
            storyTimes: c.storyTimes,
            feedTimes: c.feedTimes,
          }
        : { ...PADRAO },
    );
    setEditando(c ? c.id : 'nova');
    setAviso(null);
  }

  async function salvarForm() {
    if (!form || !editando) return;
    const limpo: ConfigChamada = {
      ...form,
      whatsappTexts: form.whatsappTexts.map((t) => t.trim()).filter(Boolean),
      feedCaptions: form.feedCaptions.map((t) => t.trim()).filter(Boolean),
      whatsappTimes: form.whatsappTimes.filter((t) => HHMM.test(t)),
      storyTimes: form.storyTimes.filter((t) => HHMM.test(t)),
      feedTimes: form.feedTimes.filter((t) => HHMM.test(t)),
    };
    await rodar('salvar', async () => {
      if (editando === 'nova') await api.criarChamada(limpo);
      else await api.salvarChamada(editando, limpo);
      setEditando(null);
      setForm(null);
      return 'Chamada salva.';
    });
  }

  /** Sobe os arquivos, agrupa pelo nome e cria uma arte por grupo. */
  async function adicionarArtes(c: Chamada, arquivos: FileList | null) {
    if (!arquivos || arquivos.length === 0) return;
    await rodar(`artes-${c.id}`, async () => {
      const grupos = new Map<string, { squareId?: string; feedId?: string; storyId?: string }>();
      for (const arquivo of Array.from(arquivos)) {
        const { base, formato } = classificarArquivo(arquivo.name);
        const midia = await api.uploadMedia(arquivo);
        const g = grupos.get(base) ?? {};
        if (formato === 'square') g.squareId = midia.id;
        else if (formato === 'feed') g.feedId = midia.id;
        else g.storyId = midia.id;
        grupos.set(base, g);
      }
      for (const g of grupos.values()) await api.adicionarArteChamada(c.id, g);
      return `${grupos.size} arte(s) adicionada(s) (${arquivos.length} arquivo(s)).`;
    });
  }

  const patch = (p: Partial<ConfigChamada>) => setForm((f) => (f ? { ...f, ...p } : f));

  return (
    <section className="card" style={{ padding: 16, marginBottom: 18 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span aria-hidden="true">📣</span>
        <p className="section-title" style={{ margin: 0, flex: 1, minWidth: 180 }}>
          Chamadas para o grupo
        </p>
        {editando === null && (
          <button
            type="button"
            className={chamadas.length === 0 ? 'primary' : 'ghost'}
            style={{ flex: '0 0 auto' }}
            onClick={() => abrirForm(null)}
          >
            + nova chamada
          </button>
        )}
      </div>
      <p className="muted" style={{ margin: '6px 0 0', fontSize: 12.5 }}>
        O convite do dia com a bonequinha: sai no grupo e no canal, nos Stories e no Feed, com
        arte e texto em rodízio. O link entra sozinho no fim da mensagem do WhatsApp e na DM de
        quem comentar no Instagram.
      </p>

      {aviso && (
        <p
          className={aviso.ok ? 'muted' : 'err'}
          style={{ margin: '8px 0 0', fontSize: 12.5, color: aviso.ok ? 'var(--gain)' : undefined }}
        >
          {aviso.texto}
        </p>
      )}

      {form && editando !== null && (
        <FormChamada
          form={form}
          patch={patch}
          salvando={ocupado === 'salvar'}
          onSalvar={() => void salvarForm()}
          onCancelar={() => {
            setEditando(null);
            setForm(null);
          }}
        />
      )}

      {chamadas.map((c) => (
        <div
          key={c.id}
          style={{
            marginTop: 12,
            padding: '12px 14px',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-control)',
            display: 'grid',
            gap: 10,
          }}
        >
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ flex: 1, minWidth: 160 }}>{c.name}</strong>
            <span className={`badge ${c.active ? 'ok' : ''}`} style={{ flex: '0 0 auto' }}>
              {c.active ? 'ligada' : 'desligada'}
            </span>
            {c.active && (
              <span className="muted" style={{ flex: '0 0 auto', fontSize: 12 }}>
                {c.pendentes} na fila
              </span>
            )}
            <button
              type="button"
              className={c.active ? '' : 'primary'}
              style={{ flex: '0 0 auto' }}
              disabled={ocupado !== null}
              onClick={() =>
                void rodar(`ativar-${c.id}`, async () => {
                  const r = await api.ativarChamada(c.id, !c.active);
                  return c.active
                    ? `Chamada desligada${r.cancelados ? ` (${r.cancelados} post(s) tirados da fila)` : ''}.`
                    : `Chamada ligada: ${r.criados} post(s) na fila para os próximos dias.`;
                })
              }
            >
              {ocupado === `ativar-${c.id}` ? <span className="spinner" /> : c.active ? 'Desligar' : 'Ligar'}
            </button>
            <button
              type="button"
              className="ghost"
              style={{ flex: '0 0 auto' }}
              onClick={() => (editando === c.id ? setEditando(null) : abrirForm(c))}
            >
              {editando === c.id ? 'fechar' : 'configurar'}
            </button>
            <button
              type="button"
              className="danger"
              style={{ flex: '0 0 auto' }}
              disabled={ocupado !== null}
              onClick={() => {
                if (!window.confirm(`Remover a chamada “${c.name}”? O que ainda ia sair é cancelado.`)) return;
                void rodar(`remover-${c.id}`, async () => {
                  await api.removerChamada(c.id);
                  return 'Chamada removida.';
                });
              }}
            >
              Remover
            </button>
          </div>

          <div className="muted" style={{ fontSize: 12.5 }}>
            💬 WhatsApp {c.whatsappTimes.join(', ') || '—'} · 📱 Stories {c.storyTimes.join(', ') || '—'} ·
            🖼️ Feed {c.feedTimes.join(', ') || '—'} · {c.whatsappTexts.length} texto(s) ·{' '}
            {c.arts.length} arte(s)
          </div>

          {/* Artes: a miniatura quadrada representa a arte; os selos dizem
              quais formatos existem, porque um formato faltando derruba o
              destino inteiro sem aviso na hora de publicar. */}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {c.arts.map((a) => (
              <MiniArte
                key={a.id}
                arte={a}
                onRemover={() =>
                  void rodar(`arte-${a.id}`, async () => {
                    await api.removerArteChamada(c.id, a.id);
                    return null;
                  })
                }
              />
            ))}
            <label
              className="ghost"
              style={{
                width: 96,
                height: 96,
                border: '1px dashed var(--line)',
                borderRadius: 'var(--r-control)',
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                fontSize: 12,
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              {ocupado === `artes-${c.id}` ? <span className="spinner" /> : '+ artes'}
              <input
                ref={arquivosRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                hidden
                onChange={(e) => {
                  void adicionarArtes(c, e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
            Selecione os arquivos de uma vez. O nome diz o formato: <span className="mono">arte1-quadrado.png</span>{' '}
            (WhatsApp), <span className="mono">arte1-feed.png</span> (4:5) e{' '}
            <span className="mono">arte1-story.png</span> (9:16) viram UMA arte.
          </p>
        </div>
      ))}
    </section>
  );
}

function MiniArte({ arte, onRemover }: { arte: ArteChamada; onRemover: () => void }) {
  const capa = arte.square ?? arte.feed ?? arte.story;
  return (
    <div style={{ position: 'relative', flex: '0 0 auto' }}>
      {capa ? (
        <img
          src={capa.publicUrl}
          alt=""
          width={96}
          height={96}
          style={{ objectFit: 'cover', borderRadius: 'var(--r-control)', display: 'block' }}
        />
      ) : (
        <div className="post-thumb" style={{ width: 96, height: 96 }} />
      )}
      <div
        className="row"
        style={{ gap: 3, position: 'absolute', left: 4, bottom: 4, fontSize: 10 }}
        title="Formatos disponíveis"
      >
        <span className={`badge ${arte.square ? 'ok' : 'err'}`}>1:1</span>
        <span className={`badge ${arte.feed ? 'ok' : 'err'}`}>4:5</span>
        <span className={`badge ${arte.story ? 'ok' : 'err'}`}>9:16</span>
      </div>
      <button
        type="button"
        className="ghost"
        aria-label="Remover arte"
        title="Remover arte"
        style={{ position: 'absolute', top: 2, right: 2, padding: '0 6px', fontSize: 11 }}
        onClick={onRemover}
      >
        ✕
      </button>
    </div>
  );
}

function ListaTextos({
  rotulo,
  valores,
  onChange,
  placeholder,
}: {
  rotulo: string;
  valores: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  return (
    <div>
      <label>{rotulo}</label>
      <div style={{ display: 'grid', gap: 6 }}>
        {valores.map((t, i) => (
          <div key={i} className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
            <textarea
              value={t}
              placeholder={placeholder}
              aria-label={`${rotulo} ${i + 1}`}
              onChange={(e) => onChange(valores.map((x, j) => (j === i ? e.target.value : x)))}
              style={{ minHeight: 64, fontSize: 12.5, flex: 1 }}
            />
            <button
              type="button"
              className="ghost"
              aria-label={`Remover texto ${i + 1}`}
              style={{ flex: '0 0 auto' }}
              onClick={() => onChange(valores.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
        {valores.length < 10 && (
          <button
            type="button"
            className="ghost"
            style={{ justifySelf: 'start' }}
            onClick={() => onChange([...valores, ''])}
          >
            + texto
          </button>
        )}
      </div>
    </div>
  );
}

function Horarios({
  rotulo,
  valores,
  onChange,
}: {
  rotulo: string;
  valores: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div>
      <label>{rotulo}</label>
      <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {valores.map((t, i) => (
          <span key={i} className="row" style={{ gap: 2, alignItems: 'center', flex: '0 0 auto' }}>
            <input
              type="time"
              value={t}
              aria-label={`${rotulo} ${i + 1}`}
              onChange={(e) => onChange(valores.map((x, j) => (j === i ? e.target.value : x)))}
              style={{ width: 110 }}
            />
            <button
              type="button"
              className="ghost"
              aria-label={`Remover o horário ${t}`}
              onClick={() => onChange(valores.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </span>
        ))}
        {valores.length < 12 && (
          <button
            type="button"
            className="ghost"
            style={{ flex: '0 0 auto' }}
            onClick={() => onChange([...valores, '15:00'])}
          >
            + horário
          </button>
        )}
        {valores.length === 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            sem horário = destino desligado
          </span>
        )}
      </div>
    </div>
  );
}

function FormChamada({
  form,
  patch,
  salvando,
  onSalvar,
  onCancelar,
}: {
  form: ConfigChamada;
  patch: (p: Partial<ConfigChamada>) => void;
  salvando: boolean;
  onSalvar: () => void;
  onCancelar: () => void;
}) {
  return (
    <div className="form-grid" style={{ gap: 14, marginTop: 14 }}>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="ch-nome">Nome</label>
          <input id="ch-nome" value={form.name} onChange={(e) => patch({ name: e.target.value })} />
        </div>
        <div style={{ flex: '2 1 280px' }}>
          <label htmlFor="ch-link">Link do convite</label>
          <input
            id="ch-link"
            value={form.link}
            onChange={(e) => patch({ link: e.target.value })}
            placeholder="https://chat.whatsapp.com/..."
          />
        </div>
      </div>

      <Horarios
        rotulo="💬 WhatsApp (grupo + canal)"
        valores={form.whatsappTimes}
        onChange={(v) => patch({ whatsappTimes: v })}
      />
      <Horarios rotulo="📱 Stories" valores={form.storyTimes} onChange={(v) => patch({ storyTimes: v })} />
      <Horarios rotulo="🖼️ Feed" valores={form.feedTimes} onChange={(v) => patch({ feedTimes: v })} />

      <ListaTextos
        rotulo="Textos do WhatsApp (em rodízio; o link entra sozinho no fim)"
        valores={form.whatsappTexts}
        onChange={(v) => patch({ whatsappTexts: v })}
        placeholder="Chama a amiga que ama achadinho pro Grupo VIP 👇"
      />
      <ListaTextos
        rotulo="Legendas do Feed (sem link: peça para comentar ou ir na bio)"
        valores={form.feedCaptions}
        onChange={(v) => patch({ feedCaptions: v })}
        placeholder="Link do Grupo VIP na bio 👆 Comenta GRUPO se você já entrou!"
      />

      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="primary"
          style={{ flex: '0 0 auto' }}
          disabled={salvando}
          onClick={onSalvar}
        >
          {salvando ? <span className="spinner" /> : 'Salvar'}
        </button>
        <button type="button" className="ghost" style={{ flex: '0 0 auto' }} onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
